<template>
    <div class="qce">
        <div
            ref="stickyAnchor"
            class="qce-sticky-anchor"
            :style="stickyPinned ? { minHeight: stickyPinHeight + 'px' } : null"
        >
        <div
            ref="stickyStack"
            class="qce-sticky-stack"
            :class="{ 'is-pinned': stickyPinned }"
            :style="stickyPinStyle"
        >
            <div class="qce-sticky" :class="{ 'qce-sticky--recurring': stickyScope === 'recurring' }">
                <div class="qce-sticky__scope" v-if="hasRecurringSals">
                    <button
                        type="button"
                        class="qce-scope-btn"
                        :class="{ 'is-active': stickyScope === 'onetime' }"
                        @click="stickyScope = 'onetime'"
                    >Una tantum</button>
                    <button
                        type="button"
                        class="qce-scope-btn"
                        :class="{ 'is-active': stickyScope === 'recurring' }"
                        @click="stickyScope = 'recurring'"
                    >Contratto</button>
                </div>
                <div v-else class="qce-sticky__kicker">
                    <span class="qce-sticky__label">Ambito</span>
                    <strong>Una tantum</strong>
                </div>
                <div class="qce-sticky__item">
                    <span class="qce-sticky__label">Ore</span>
                    <strong>{{ formatHours(stickyTotals.hours) }}</strong>
                </div>
                <div class="qce-sticky__item">
                    <span class="qce-sticky__label">Costo</span>
                    <strong>€ {{ formatMoney(stickyTotals.actualCost) }}</strong>
                </div>
                <div class="qce-sticky__item" v-if="stickyScope === 'onetime'">
                    <span class="qce-sticky__label">Sconto tot.</span>
                    <div class="qce-money qce-money--dark">
                        <span aria-hidden="true">€</span>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            class="form-control input-sm qce-sticky__input"
                            title="Sconto totale offerta (€)"
                            v-model.number="headerDiscount"
                            @input="touch"
                        >
                    </div>
                </div>
                <div class="qce-sticky__item" v-else>
                    <span class="qce-sticky__label">Sconto righe</span>
                    <strong>€ {{ formatMoney(stickyTotals.lineDiscount) }}</strong>
                </div>
                <div class="qce-sticky__item qce-sticky__item--revenue">
                    <span class="qce-sticky__label">Netto</span>
                    <strong>€ {{ formatMoney(stickyTotals.discountedRevenue) }}</strong>
                </div>
                <div
                    class="qce-sticky__item"
                    :class="'qce-sticky__item--' + gmTone(stickyTotals.gmPercent, stickyTotals.actualCost, stickyTotals.discountedRevenue)"
                >
                    <span class="qce-sticky__label">Margine</span>
                    <strong>€ {{ formatMoney(stickyTotals.margin) }}</strong>
                </div>
                <div
                    class="qce-sticky__item"
                    :class="'qce-sticky__item--' + gmTone(stickyTotals.gmPercent, stickyTotals.actualCost, stickyTotals.discountedRevenue)"
                >
                    <span class="qce-sticky__label">GM%</span>
                    <strong>{{ formatGm(stickyTotals.gmPercent, stickyTotals.actualCost, stickyTotals.discountedRevenue) }}</strong>
                </div>
                <div class="qce-sticky__actions">
                    <span v-if="dirty" class="qce-dirty">Non salvato</span>
                    <button type="button" class="btn btn-sm btn-link qce-recalc-btn" @click="openRecalcModal">
                        Ricalcola
                    </button>
                    <button type="button" class="btn btn-sm btn-success" @click="submitQuote">
                        Salva
                    </button>
                </div>
            </div>
            <p v-if="stickyScope === 'recurring'" class="qce-sticky__caption">
                Valore contratto (ore, costi e ricavi × numero di periodi; max {{ maxRecurringPeriods }}).
                Le righe sotto restano per periodo.
            </p>
        </div>
        </div>

        <p class="qce-hint text-muted">
            Salvando si aggiorna l’offerta collegata.
        </p>

        <div v-if="showRecalcModal" class="qce-modal-backdrop" @click.self="closeRecalcModal">
            <div class="qce-modal" role="dialog" aria-modal="true">
                <div class="qce-modal__header">
                    <h4>Ricalcola ricavi</h4>
                    <button type="button" class="close" @click="closeRecalcModal">&times;</button>
                </div>
                <div class="qce-modal__body">
                    <p>Sovrascrive i ricavi di tutte le attività non opzionali. Salva dopo per persistarli sull’offerta.</p>
                    <div class="form-group">
                        <label>Modalità *</label>
                        <select class="form-control" v-model="recalcForm.mode">
                            <option value="ratecard">Ratecard + markup su costi vivi</option>
                            <option value="markup">Markup</option>
                        </select>
                        <small class="help-block" v-if="recalcForm.mode === 'ratecard'">
                            Ore: tariffa ratecard × ore. Costi vivi: markup sul costo.
                        </small>
                        <small class="help-block" v-else>
                            Ore: costo orario risorsa × (1 + markup%). Costi vivi: stesso markup.
                        </small>
                    </div>
                    <div class="form-group">
                        <label>Markup (%) *</label>
                        <input type="number" class="form-control" min="0" step="0.01" v-model.number="recalcForm.markup">
                    </div>
                    <div class="form-group" v-if="enableRatecardRange && recalcForm.mode === 'ratecard'">
                        <label>Percentuale ratecard (0% = min, 100% = max)</label>
                        <div class="qce-modal__range">
                            <input type="range" min="0" max="100" step="1" v-model.number="recalcForm.ratecardPercentage">
                            <span>{{ recalcForm.ratecardPercentage }}%</span>
                        </div>
                    </div>
                    <div v-if="recalcPreview" class="qce-recalc-preview">
                        Ricavi netti attuali € {{ formatMoney(recalcPreview.before) }}
                        → € {{ formatMoney(recalcPreview.after) }}
                        <strong :class="recalcPreview.delta >= 0 ? 'qce-gm--ok' : 'qce-gm--bad'">
                            ({{ recalcPreview.delta >= 0 ? '+' : '' }}€ {{ formatMoney(recalcPreview.delta) }})
                        </strong>
                    </div>
                </div>
                <div class="qce-modal__footer">
                    <button type="button" class="btn btn-default" @click="closeRecalcModal">Annulla</button>
                    <button type="button" class="btn btn-warning" @click="applyRecalc">Sovrascrivi ricavi</button>
                </div>
            </div>
        </div>

        <draggable
            v-model="sals"
            handle=".qce-sal-drag"
            :animation="150"
            ghost-class="qce-sal--ghost"
            @end="onSalsReorder"
        >
            <div
                v-for="(sal, salIndex) in sals"
                :key="sal.client_key"
                class="qce-sal"
                :class="{
                    'qce-sal--optional': sal.is_optional,
                    'qce-sal--recurring': sal.is_recurring,
                }"
            >
                <div class="qce-sal__header">
                    <span class="qce-sal-drag" title="Trascina per riordinare il SAL">
                        <i class="fa fa-arrows"></i>
                    </span>
                    <div class="qce-sal__title-wrap">
                        <label>Titolo SAL *</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="sal.title"
                            required
                            @input="touch"
                        >
                    </div>
                    <div class="qce-sal__flags">
                        <label class="checkbox-inline">
                            <input type="checkbox" v-model="sal.is_recurring" @change="touch"> Ricorrente
                        </label>
                        <label class="checkbox-inline">
                            <input type="checkbox" v-model="sal.is_optional" @change="touch"> Opzionale
                        </label>
                        <label class="checkbox-inline qce-sal__flag--quiet">
                            <input type="checkbox" v-model="sal.hide_price" @change="touch"> Nascondi prezzo
                        </label>
                        <button type="button" class="btn btn-xs btn-danger qce-sal__delete" @click="removeSal(salIndex)" title="Elimina SAL">
                            <i class="fa fa-trash"></i>
                        </button>
                    </div>
                </div>

                <div v-if="sal.is_recurring" class="qce-sal__recurring">
                    <select class="form-control input-sm" v-model="sal.recurring_period" @change="onSalRecurringDatesEdited(salIndex)">
                        <option value="">Periodo…</option>
                        <option value="monthly">Mensile</option>
                        <option value="quarterly">Trimestrale</option>
                        <option value="semi-annual">Semestrale</option>
                        <option value="annual">Annuale</option>
                    </select>
                    <input type="text" class="form-control input-sm" placeholder="Inizio d/m/Y" v-model="sal.recurring_start_date" @input="onSalRecurringDatesEdited(salIndex)">
                    <input type="text" class="form-control input-sm" placeholder="Fine d/m/Y" v-model="sal.recurring_end_date" @input="onSalRecurringDatesEdited(salIndex)">
                    <label class="checkbox-inline">
                        <input type="checkbox" v-model="sal.recurring_advance" @change="onSalRecurringDatesEdited(salIndex)"> Anticipata
                    </label>
                    <span class="qce-period-badge" v-if="sal.recurring_period && sal.recurring_start_date && sal.recurring_end_date">
                        {{ periodLabel(null, sal) }}
                    </span>
                    <button
                        v-if="salRecurringApplyPrompt[sal.client_key] && salHasActivities(sal)"
                        type="button"
                        class="btn btn-xs btn-primary qce-sal__apply-recurring"
                        @click="applySalRecurringToAll(salIndex)"
                    >
                        Applica a tutte le attività
                    </button>
                </div>

                <div class="qce-colhead" aria-hidden="false">
                    <span></span>
                    <span></span>
                    <span>Prodotto</span>
                    <span>Attività</span>
                    <span>Risorsa</span>
                    <span>Qty</span>
                    <span>Unità</span>
                    <span class="qce-colhead--num">Costo</span>
                    <span class="qce-colhead--num">Ricavo</span>
                    <span class="qce-colhead--num">Sconto</span>
                    <span class="qce-colhead--num">GM</span>
                    <span></span>
                </div>

                <draggable
                    v-model="sal.activityBlocks"
                    group="quote-activity-blocks"
                    handle=".qce-act-drag"
                    :animation="150"
                    ghost-class="qce-activity-block--ghost"
                    class="qce-activities"
                    @end="onActivityBlocksReorder"
                >
                    <div
                        v-for="(block, blockIndex) in sal.activityBlocks"
                        :key="block.key"
                        class="qce-activity-block"
                    >
                        <div
                            v-for="(activity, rowIndex) in block.activities"
                            :key="activity.client_key"
                            class="qce-activity"
                            :class="{
                                'qce-activity--optional': activity.is_optional,
                                'qce-activity--open': rowIndex === 0 && isDetailsOpen(activity.client_key),
                                'qce-activity--continuation': rowIndex > 0,
                            }"
                        >
                            <div class="qce-activity__main">
                                <span
                                    v-if="rowIndex === 0"
                                    class="qce-act-drag"
                                    title="Trascina per riordinare o spostare in un altro SAL"
                                >
                                    <i class="fa fa-arrows"></i>
                                </span>
                                <span v-else class="qce-act-drag-spacer" aria-hidden="true"></span>

                                <button
                                    v-if="rowIndex === 0"
                                    type="button"
                                    class="btn btn-xs btn-default qce-activity__toggle"
                                    :title="isDetailsOpen(activity.client_key) ? 'Nascondi dettagli' : 'Mostra dettagli'"
                                    @click="toggleDetails(activity.client_key)"
                                >
                                    <i class="fa" :class="isDetailsOpen(activity.client_key) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                </button>
                                <span v-else class="qce-activity__toggle-spacer" title="Risorsa aggiuntiva"></span>

                                <select
                                    class="form-control input-sm qce-field-product"
                                    v-model="activity.product_id"
                                    :disabled="rowIndex !== 0"
                                    @change="onProductOrTitleChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                >
                                    <option :value="null">Prodotto…</option>
                                    <option v-for="p in products" :key="'p-'+p.id" :value="p.id">{{ p.name }}</option>
                                </select>
                                <input
                                    type="text"
                                    class="form-control input-sm qce-field-title"
                                    placeholder="Attività"
                                    v-model="activity.title"
                                    :readonly="rowIndex !== 0"
                                    @input="onProductOrTitleChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                >
                                <select
                                    class="form-control input-sm qce-field-resource"
                                    v-model="activity.cost_id"
                                    @change="onActivityMetaChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                >
                                    <option :value="null">Risorsa…</option>
                                    <option v-for="c in costs" :key="'c-'+c.id" :value="c.id">{{ c.title }}</option>
                                </select>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    class="form-control input-sm qce-qty"
                                    :title="isOverhead(activity) ? ('Overhead ' + overheadPercent(activity) + '% ore SAL') : 'Valore risorsa'"
                                    :placeholder="isOverhead(activity) ? 'Auto' : 'Valore'"
                                    v-model.number="activity.quantity"
                                    :readonly="isOverhead(activity)"
                                    :disabled="isOverhead(activity)"
                                    @input="onActivityMetaChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                >
                                <select
                                    class="form-control input-sm qce-unit"
                                    title="Unità di misura"
                                    :disabled="!isTimeBased(activity) || isOverhead(activity)"
                                    v-model="activity.unit"
                                    @change="onActivityMetaChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                >
                                    <option
                                        v-for="u in unitOptionsFor(activity)"
                                        :key="u.value"
                                        :value="u.value"
                                    >{{ u.label }}</option>
                                </select>

                                <span
                                    class="qce-cost"
                                    title="Costo effettivo (tariffa risorsa × ore + costi vivi)"
                                >€ {{ formatMoney(rowIndex === 0 ? groupLineCost(sal, activity) : lineCost(activity)) }}</span>

                                <div class="qce-money" v-if="rowIndex === 0">
                                    <span aria-hidden="true">€</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        class="form-control input-sm qce-revenue"
                                        title="Ricavo (unificato per attività)"
                                        placeholder="0"
                                        v-model.number="activity.revenue"
                                        @input="onGroupRevenueChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                    >
                                </div>
                                <span
                                    v-else
                                    class="qce-revenue qce-revenue--shared"
                                    title="Ricavo unificato sull'attività principale"
                                >€ {{ formatMoney(groupPrimary(sal, activity).revenue) }}</span>

                                <div class="qce-money" v-if="rowIndex === 0">
                                    <span aria-hidden="true">€</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        class="form-control input-sm qce-discount"
                                        title="Sconto riga (€)"
                                        placeholder="0"
                                        v-model.number="activity.line_discount"
                                        @input="onGroupCommercialChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                    >
                                </div>
                                <span v-else class="qce-discount-spacer" aria-hidden="true">—</span>
                                <span
                                    v-if="rowIndex === 0"
                                    class="qce-gm"
                                    :class="lineGmClass(sal, activity)"
                                    :title="lineGmTitle(sal, activity)"
                                >{{ formatGm(lineGm(sal, activity), groupLineCost(sal, activity), activityNetRevenue(groupPrimary(sal, activity))) }}</span>
                                <span v-else class="qce-gm qce-gm--muted" aria-hidden="true">—</span>

                                <div class="qce-activity__actions">
                                    <button
                                        v-if="rowIndex === 0 && !isOverhead(activity)"
                                        type="button"
                                        class="btn btn-xs btn-success"
                                        title="Aggiungi risorsa"
                                        @click="addResource(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                    >
                                        <i class="fa fa-user-plus"></i>
                                    </button>
                                    <span v-else class="qce-action-spacer" aria-hidden="true"></span>
                                    <button
                                        type="button"
                                        class="btn btn-xs btn-danger"
                                        title="Elimina"
                                        @click="removeActivity(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                    >
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </div>
                            </div>

                            <div
                                v-if="rowIndex === 0"
                                v-show="isDetailsOpen(activity.client_key)"
                                class="qce-activity__details"
                            >
                                <div class="qce-activity__details-row">
                                    <span class="qce-computed" title="Costo calcolato">
                                        {{ formatHours(activity.total_hours) }} h · € {{ formatMoney(activity.total_price) }}
                                        <small v-if="activity.total_cost"> / vivi € {{ formatMoney(activity.total_cost) }}</small>
                                    </span>
                                    <label class="checkbox-inline">
                                        <input
                                            type="checkbox"
                                            v-model="activity.is_optional"
                                            @change="onGroupCommercialChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                        > Opzionale
                                    </label>
                                    <label class="checkbox-inline">
                                        <input
                                            type="checkbox"
                                            v-model="activity.hide_price"
                                            @change="onGroupCommercialChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                        > Nascondi prezzo
                                    </label>
                                    <label class="qce-notes-label">Note
                                        <input
                                            type="text"
                                            class="form-control input-sm"
                                            v-model="activity.notes"
                                            @input="onGroupCommercialChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                        >
                                    </label>
                                </div>

                                <div v-if="sal.is_recurring" class="qce-activity__recurring">
                                    <small class="text-muted">Override ricorrenza attività:</small>
                                    <select
                                        class="form-control input-sm"
                                        v-model="activity.recurring_period"
                                        @change="onGroupCommercialChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                    >
                                        <option value="">Eredita dal SAL</option>
                                        <option value="monthly">Mensile</option>
                                        <option value="quarterly">Trimestrale</option>
                                        <option value="semi-annual">Semestrale</option>
                                        <option value="annual">Annuale</option>
                                    </select>
                                    <input
                                        type="text"
                                        class="form-control input-sm"
                                        placeholder="Inizio"
                                        v-model="activity.recurring_start_date"
                                        @input="onGroupCommercialChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                    >
                                    <input
                                        type="text"
                                        class="form-control input-sm"
                                        placeholder="Fine"
                                        v-model="activity.recurring_end_date"
                                        @input="onGroupCommercialChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                    >
                                    <label class="checkbox-inline">
                                        <input
                                            type="checkbox"
                                            v-model="activity.recurring_advance"
                                            @change="onGroupCommercialChange(salIndex, actIndexInSal(sal, blockIndex, rowIndex))"
                                        > Anticipata
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </draggable>

                <div class="qce-sal__footer">
                    <button type="button" class="btn btn-sm btn-info" @click="addActivity(salIndex)">
                        <i class="fa fa-plus"></i> Nuova attività
                    </button>
                    <span class="qce-sal__totals">
                        <span v-if="sal.is_optional" class="qce-pill qce-pill--optional">Escluso dai KPI</span>
                        <template v-if="sal.is_recurring">
                            Per periodo ({{ periodLabel(null, sal) }}):
                            {{ formatHours(salTotals(sal).hours) }} h ·
                            costo € {{ formatMoney(salTotals(sal).actualCost) }} ·
                            ricavi € {{ formatMoney(salTotals(sal).discountedRevenue) }}
                            · GM {{ formatGm(salTotals(sal).gmPercent, salTotals(sal).actualCost, salTotals(sal).discountedRevenue) }}
                            <span class="qce-sal__contract">
                                Contratto:
                                {{ formatHours(salContractTotals(sal).hours) }} h ·
                                € {{ formatMoney(salContractTotals(sal).discountedRevenue) }}
                            </span>
                        </template>
                        <template v-else>
                            Totale SAL:
                            {{ formatHours(salTotals(sal).hours) }} h ·
                            costo € {{ formatMoney(salTotals(sal).actualCost) }} ·
                            ricavi € {{ formatMoney(salTotals(sal).discountedRevenue) }}
                            · GM {{ formatGm(salTotals(sal).gmPercent, salTotals(sal).actualCost, salTotals(sal).discountedRevenue) }}
                        </template>
                    </span>
                </div>
            </div>
        </draggable>

        <button type="button" class="btn btn-block btn-primary qce-add-sal" @click="addSal">
            <i class="fa fa-plus"></i> Nuovo SAL
        </button>

        <!-- Hidden fields for classic form POST -->
        <div class="qce-hidden-fields" aria-hidden="true">
            <input type="hidden" name="total_hours" :value="totals.hours">
            <input type="hidden" name="total_price" :value="totals.hoursCost">
            <input type="hidden" name="total_cost" :value="totals.liveCost">
            <input type="hidden" name="header_discount" :value="headerDiscount">

            <template v-for="(sal, salIndex) in sals">
                <input v-if="sal.id" :key="'sid-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][id]'" :value="sal.id">
                <input :key="'snew-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][is_new]'" :value="sal.is_new ? 1 : 0">
                <input :key="'spos-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][position]'" :value="salIndex">
                <input :key="'stitle-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][title]'" :value="sal.title">
                <input :key="'srec-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][is_recurring]'" :value="sal.is_recurring ? 1 : 0">
                <input :key="'srp-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][recurring_period]'" :value="sal.recurring_period || ''">
                <input :key="'srs-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][recurring_start_date]'" :value="sal.recurring_start_date || ''">
                <input :key="'sre-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][recurring_end_date]'" :value="sal.recurring_end_date || ''">
                <input :key="'sra-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][recurring_advance]'" :value="sal.recurring_advance ? 1 : 0">
                <input :key="'sth-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][salTotalHours]'" :value="salTotals(sal).hours">
                <input :key="'stc-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][salTotalCost]'" :value="salTotals(sal).liveCost">
                <input :key="'stp-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][salTotalPrice]'" :value="salTotals(sal).hoursCost">
                <!-- Sempre nested: nuovo SAL non ha ancora id per commercial_sals[...] -->
                <input :key="'sopt-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][is_optional]'" :value="sal.is_optional ? 1 : 0">
                <input :key="'shide-'+sal.client_key" type="hidden" :name="'sals['+salIndex+'][hide_price]'" :value="sal.hide_price ? 1 : 0">

                <input v-if="sal.id" :key="'cso-'+sal.client_key" type="hidden" :name="'commercial_sals['+sal.id+'][is_optional]'" :value="sal.is_optional ? 1 : 0">
                <input v-if="sal.id" :key="'csh-'+sal.client_key" type="hidden" :name="'commercial_sals['+sal.id+'][hide_price]'" :value="sal.hide_price ? 1 : 0">

                <template v-for="(activity, actIndex) in sal.activities">
                    <input v-if="activity.id" :key="'aid-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][id]'" :value="activity.id">
                    <input :key="'anew-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][is_new]'" :value="activity.is_new ? 1 : 0">
                    <input :key="'apos-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][position]'" :value="actIndex">
                    <input :key="'apid-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][product_id]'" :value="activity.product_id || ''">
                    <input :key="'atitle-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][title]'" :value="activity.title">
                    <input :key="'acid-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][cost_id]'" :value="activity.cost_id || ''">
                    <input :key="'aqty-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][quantity]'" :value="activity.quantity">
                    <input :key="'aunit-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][unit]'" :value="activity.unit || 'hours'">
                    <input :key="'anotes-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][notes]'" :value="activity.notes || ''">
                    <input :key="'agrp-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][activity_group_id]'" :value="activity.activity_group_id || ''">
                    <input :key="'ath-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][salActivityTotalHours]'" :value="activity.total_hours">
                    <input :key="'atc-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][salActivityTotalCost]'" :value="activity.total_cost">
                    <input :key="'atp-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][salActivityTotalPrice]'" :value="activity.total_price">
                    <input :key="'arp-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][recurring_period]'" :value="activity.recurring_period || ''">
                    <input :key="'ars-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][recurring_start_date]'" :value="activity.recurring_start_date || ''">
                    <input :key="'are-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][recurring_end_date]'" :value="activity.recurring_end_date || ''">
                    <input :key="'ara-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][recurring_advance]'" :value="activity.recurring_advance ? 1 : 0">
                    <!-- Nested anche senza id: altrimenti ricavo/opz. delle nuove voci si perdono al primo save -->
                    <input :key="'arev-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][revenue]'" :value="isGroupPrimary(sal, actIndex) ? activity.revenue : 0">
                    <input :key="'ald-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][line_discount]'" :value="isGroupPrimary(sal, actIndex) ? activity.line_discount : 0">
                    <input :key="'aopt-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][is_optional]'" :value="activity.is_optional ? 1 : 0">
                    <input :key="'ahide-'+activity.client_key" type="hidden" :name="'sals['+salIndex+'][sal_activities]['+actIndex+'][hide_price]'" :value="activity.hide_price ? 1 : 0">

                    <template v-if="activity.id">
                        <input :key="'cr-'+activity.client_key" type="hidden" :name="'commercial_activities['+activity.id+'][revenue]'" :value="isGroupPrimary(sal, actIndex) ? activity.revenue : 0">
                        <input :key="'cd-'+activity.client_key" type="hidden" :name="'commercial_activities['+activity.id+'][line_discount]'" :value="isGroupPrimary(sal, actIndex) ? activity.line_discount : 0">
                        <input :key="'co-'+activity.client_key" type="hidden" :name="'commercial_activities['+activity.id+'][is_optional]'" :value="activity.is_optional ? 1 : 0">
                        <input :key="'ch-'+activity.client_key" type="hidden" :name="'commercial_activities['+activity.id+'][hide_price]'" :value="activity.hide_price ? 1 : 0">
                    </template>
                </template>
            </template>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable';
import {
    recalculateActivity,
    recalculateSalActivities,
    computeQuoteTotals,
    computeRecurringQuoteTotals,
    computeSalTotals,
    formatMoney,
    formatHours,
    findCost,
    findProduct,
    newClientKey,
    activityGroupId,
    activitiesToBlocks,
    blocksToActivities,
    isActivityGroupPrimary,
    isOverheadActivity,
    applyPricingToRevenues,
    actualLineCost,
    groupActualCost,
    activityNetRevenue,
    lineMarginPercent,
    gmTone,
    gmToneClass,
    recurringPeriodLabel,
    maxRecurringPeriodCount,
    applySalRecurringToActivities,
    previewPricingDelta,
} from './quoteTotals';
import { computeStickyPinState } from './stickyPin';

function emptyActivity(defaultProductId) {
    return {
        id: null,
        client_key: newClientKey('a'),
        position: 0,
        product_id: defaultProductId || null,
        title: '',
        cost_id: null,
        quantity: 0,
        unit: 'hours',
        notes: '',
        activity_group_id: null,
        is_new: true,
        total_hours: 0,
        total_cost: 0,
        total_price: 0,
        recurring_period: '',
        recurring_start_date: '',
        recurring_end_date: '',
        recurring_advance: false,
        revenue: 0,
        line_discount: 0,
        is_optional: false,
        hide_price: false,
    };
}

function emptySal() {
    return {
        id: null,
        client_key: newClientKey('s'),
        position: 0,
        title: '',
        is_new: true,
        is_recurring: false,
        recurring_period: '',
        recurring_start_date: '',
        recurring_end_date: '',
        recurring_advance: false,
        is_optional: false,
        hide_price: false,
        total_hours: 0,
        total_cost: 0,
        total_price: 0,
        activities: [],
    };
}

export default {
    name: 'QuoteCommercialEditor',
    components: {
        draggable,
    },
    props: {
        initial: {
            type: Object,
            default: null,
        },
    },
    data() {
        const initial = this.initial || window.__quoteCommercialEditorInitial || {};
        const sals = JSON.parse(JSON.stringify(initial.sals || [])).map((sal) => {
            sal.activityBlocks = activitiesToBlocks(sal.activities || []);
            return sal;
        });
        return {
            products: initial.products || [],
            costs: initial.costs || [],
            units: initial.units || [],
            defaultProductId: initial.default_product_id || null,
            defaultMarkup: initial.default_markup != null ? Number(initial.default_markup) : 30,
            enableRatecardRange: !!initial.enable_ratecard_range,
            defaultRatecardPercentage: initial.default_ratecard_percentage != null
                ? Number(initial.default_ratecard_percentage)
                : 100,
            sals,
            headerDiscount: initial.header_discount != null ? Number(initial.header_discount) : 0,
            revision: 0,
            dirty: false,
            salRecurringApplyPrompt: {},
            stickyScope: 'onetime',
            stickyPinned: false,
            stickyPinHeight: 0,
            stickyPinStyle: {},
            openDetails: {},
            showRecalcModal: false,
            recalcForm: {
                mode: 'ratecard',
                markup: initial.default_markup != null ? Number(initial.default_markup) : 30,
                ratecardPercentage: initial.default_ratecard_percentage != null
                    ? Number(initial.default_ratecard_percentage)
                    : 100,
            },
        };
    },
    computed: {
        totals() {
            // eslint-disable-next-line no-unused-expressions
            this.revision;
            return computeQuoteTotals(this.sals, this.costs, this.headerDiscount);
        },
        hasRecurringSals() {
            return (this.sals || []).some((sal) => !!sal.is_recurring && !sal.is_optional);
        },
        recurringTotals() {
            // eslint-disable-next-line no-unused-expressions
            this.revision;
            return computeRecurringQuoteTotals(this.sals, this.costs);
        },
        stickyTotals() {
            // eslint-disable-next-line no-unused-expressions
            this.revision;
            if (this.stickyScope === 'recurring' && this.hasRecurringSals) {
                return this.recurringTotals;
            }
            return this.totals;
        },
        maxRecurringPeriods() {
            // eslint-disable-next-line no-unused-expressions
            this.revision;
            return maxRecurringPeriodCount(this.sals);
        },
        recalcPreview() {
            if (!this.showRecalcModal) {
                return null;
            }
            const markup = Number(this.recalcForm.markup);
            if (Number.isNaN(markup) || markup < 0) {
                return null;
            }
            return previewPricingDelta(this.sals, this.costs, {
                mode: this.recalcForm.mode,
                markup,
                ratecardPercentage: this.recalcForm.ratecardPercentage,
                ratecardRangeEnabled: this.enableRatecardRange,
            });
        },
    },
    watch: {
        stickyScope() {
            this.$nextTick(() => this.updateStickyPin());
        },
    },
    mounted() {
        this._hydrating = true;
        this.sals.forEach((sal, si) => {
            this.refreshSal(si);
        });
        this._hydrating = false;
        this.dirty = false;
        this.bindStickyPin();
        const form = this.$el && this.$el.closest ? this.$el.closest('form') : null;
        if (! form) {
            return;
        }
        this._onFormSubmit = () => {
            (this.sals || []).forEach((_, salIndex) => {
                this.syncActivitiesFromBlocks(salIndex);
            });
        };
        form.addEventListener('submit', this._onFormSubmit);
    },
    beforeDestroy() {
        this.unbindStickyPin();
        const form = this.$el && this.$el.closest ? this.$el.closest('form') : null;
        if (form && this._onFormSubmit) {
            form.removeEventListener('submit', this._onFormSubmit);
        }
    },
    methods: {
        formatMoney,
        formatHours,
        activityNetRevenue,
        gmTone,
        bindStickyPin() {
            this._onStickyPin = () => {
                if (this._stickyPinRaf) {
                    return;
                }
                this._stickyPinRaf = window.requestAnimationFrame(() => {
                    this._stickyPinRaf = 0;
                    this.updateStickyPin();
                });
            };
            this._stickyScrollParents = [
                window,
                document,
                document.querySelector('.content-wrapper'),
                document.querySelector('.wrapper'),
            ].filter(Boolean);
            this._stickyScrollParents.forEach((el) => {
                el.addEventListener('scroll', this._onStickyPin, { passive: true });
            });
            window.addEventListener('resize', this._onStickyPin);
            if (window.ResizeObserver && this.$refs.stickyAnchor) {
                this._stickyResizeObserver = new ResizeObserver(this._onStickyPin);
                this._stickyResizeObserver.observe(this.$refs.stickyAnchor);
            }
            if (window.jQuery) {
                window.jQuery(document).on('expanded.pushMenu.qce collapsed.pushMenu.qce', this._onStickyPin);
            }
            this.$nextTick(() => this.updateStickyPin());
        },
        unbindStickyPin() {
            if (this._stickyPinRaf) {
                window.cancelAnimationFrame(this._stickyPinRaf);
                this._stickyPinRaf = 0;
            }
            (this._stickyScrollParents || []).forEach((el) => {
                el.removeEventListener('scroll', this._onStickyPin);
            });
            window.removeEventListener('resize', this._onStickyPin);
            if (this._stickyResizeObserver) {
                this._stickyResizeObserver.disconnect();
                this._stickyResizeObserver = null;
            }
            if (window.jQuery && this._onStickyPin) {
                window.jQuery(document).off('expanded.pushMenu.qce collapsed.pushMenu.qce', this._onStickyPin);
            }
        },
        headerBottom() {
            const header = document.querySelector('.main-header');
            if (! header) {
                return 0;
            }
            return Math.round(header.getBoundingClientRect().bottom);
        },
        updateStickyPin() {
            const anchor = this.$refs.stickyAnchor;
            const stack = this.$refs.stickyStack;
            if (! anchor || ! stack) {
                return;
            }
            const measureEl = this.stickyPinned ? anchor : stack;
            const rect = measureEl.getBoundingClientRect();
            const next = computeStickyPinState({
                headerBottom: this.headerBottom(),
                sentinelTop: rect.top,
                sentinelLeft: rect.left,
                sentinelWidth: rect.width,
                stackHeight: stack.offsetHeight,
            });
            this.stickyPinned = next.pinned;
            this.stickyPinHeight = next.height;
            this.stickyPinStyle = next.style;
        },
        formatPercent(value) {
            if (value === null || value === undefined || Number.isNaN(Number(value))) {
                return '—';
            }
            return (Number(value)).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 1 }) + '%';
        },
        formatGm(gmPercent, actualCost, netRevenue) {
            const tone = gmTone(gmPercent, actualCost, netRevenue);
            if (tone === 'muted') {
                return '—';
            }
            if (tone === 'pure') {
                return 'n/d';
            }
            return this.formatPercent(gmPercent);
        },
        periodLabel(activity, sal) {
            return recurringPeriodLabel(activity, sal);
        },
        salTotals(sal) {
            return computeSalTotals(sal, this.costs);
        },
        salContractTotals(sal) {
            return computeSalTotals(sal, this.costs, { scaleByPeriods: true });
        },
        submitQuote() {
            (this.sals || []).forEach((_, salIndex) => {
                this.syncActivitiesFromBlocks(salIndex);
            });
            const form = this.$el && this.$el.closest ? this.$el.closest('form') : null;
            if (!form) {
                return;
            }
            if (typeof form.requestSubmit === 'function') {
                form.requestSubmit();
                return;
            }
            form.submit();
        },
        lineCost(activity) {
            return actualLineCost(activity, this.costs);
        },
        groupLineCost(sal, activity) {
            return groupActualCost(sal.activities || [], this.costs, activity);
        },
        lineGm(sal, activity) {
            const primary = this.groupPrimary(sal, activity);
            return lineMarginPercent(activityNetRevenue(primary), this.groupLineCost(sal, activity));
        },
        lineGmClass(sal, activity) {
            const primary = this.groupPrimary(sal, activity);
            return gmToneClass(gmTone(
                this.lineGm(sal, activity),
                this.groupLineCost(sal, activity),
                activityNetRevenue(primary)
            ));
        },
        lineGmTitle(sal, activity) {
            const tone = gmTone(
                this.lineGm(sal, activity),
                this.groupLineCost(sal, activity),
                activityNetRevenue(this.groupPrimary(sal, activity))
            );
            if (tone === 'pure') {
                return 'Costo 0 — ricavo puro (GM non significativa)';
            }
            return 'Margine % sul prezzo netto della riga (<15% basso, 15–30% attenzione, >30% ok)';
        },
        touch() {
            this.revision += 1;
            if (!this._hydrating) {
                this.dirty = true;
            }
        },
        salHasActivities(sal) {
            return (sal.activities || []).length > 0
                || (sal.activityBlocks || []).some((block) => (block.rows || []).length > 0);
        },
        onSalRecurringDatesEdited(salIndex) {
            const sal = this.sals[salIndex];
            this.touch();
            if (this.salHasActivities(sal)) {
                this.$set(this.salRecurringApplyPrompt, sal.client_key, true);
            }
        },
        applySalRecurringToAll(salIndex) {
            const sal = this.sals[salIndex];
            applySalRecurringToActivities(sal);
            this.$set(this.salRecurringApplyPrompt, sal.client_key, false);
            this.refreshSal(salIndex);
            this.touch();
        },
        rebuildActivityBlocks(salIndex) {
            const sal = this.sals[salIndex];
            this.$set(sal, 'activityBlocks', activitiesToBlocks(sal.activities || []));
        },
        syncActivitiesFromBlocks(salIndex) {
            const sal = this.sals[salIndex];
            this.$set(sal, 'activities', blocksToActivities(sal.activityBlocks || []));
        },
        actIndexInSal(sal, blockIndex, rowIndex) {
            let idx = 0;
            const blocks = sal.activityBlocks || [];
            for (let b = 0; b < blockIndex; b += 1) {
                idx += (blocks[b].activities || []).length;
            }
            return idx + rowIndex;
        },
        onSalsReorder() {
            this.touch();
        },
        onActivityBlocksReorder() {
            // Cross-SAL: vuedraggable aggiorna i due activityBlocks; sincronizzare
            // activities di tutti i SAL altrimenti i hidden del form restano sul SAL sorgente.
            this.sals.forEach((_, salIndex) => {
                this.syncActivitiesFromBlocks(salIndex);
                const sal = this.sals[salIndex];
                const updated = recalculateSalActivities(sal.activities, this.costs, this.products);
                this.$set(sal, 'activities', updated);
                this.$set(sal, 'activityBlocks', activitiesToBlocks(updated));
            });
            this.touch();
        },
        openRecalcModal() {
            this.recalcForm = {
                mode: 'ratecard',
                markup: this.defaultMarkup,
                ratecardPercentage: this.defaultRatecardPercentage,
            };
            this.showRecalcModal = true;
        },
        closeRecalcModal() {
            this.showRecalcModal = false;
        },
        applyRecalc() {
            const markup = Number(this.recalcForm.markup);
            if (Number.isNaN(markup) || markup < 0) {
                return;
            }
            applyPricingToRevenues(this.sals, this.costs, {
                mode: this.recalcForm.mode,
                markup,
                ratecardPercentage: this.recalcForm.ratecardPercentage,
                ratecardRangeEnabled: this.enableRatecardRange,
            });
            this.touch();
            this.closeRecalcModal();
        },
        isDetailsOpen(clientKey) {
            return !!this.openDetails[clientKey];
        },
        toggleDetails(clientKey) {
            this.$set(this.openDetails, clientKey, !this.openDetails[clientKey]);
        },
        isGroupPrimary(sal, actIndex) {
            return isActivityGroupPrimary(sal.activities || [], actIndex);
        },
        groupPrimary(sal, activity) {
            const gid = activityGroupId(activity);
            if (!gid) {
                return activity;
            }
            const found = (sal.activities || []).find((row) => activityGroupId(row) === gid);
            return found || activity;
        },
        groupIndexes(sal, actIndex) {
            const activity = sal.activities[actIndex];
            const gid = activityGroupId(activity);
            if (!gid) {
                return [actIndex];
            }
            return (sal.activities || [])
                .map((row, idx) => (activityGroupId(row) === gid ? idx : -1))
                .filter((idx) => idx >= 0);
        },
        syncGroupIdentity(salIndex, actIndex) {
            const sal = this.sals[salIndex];
            const primary = sal.activities[actIndex];
            this.groupIndexes(sal, actIndex).forEach((idx) => {
                if (idx === actIndex) {
                    return;
                }
                const row = sal.activities[idx];
                row.product_id = primary.product_id;
                row.title = primary.title;
                this.$set(sal.activities, idx, recalculateActivity(row, this.costs, this.products));
            });
        },
        onProductOrTitleChange(salIndex, actIndex) {
            if (!this.isGroupPrimary(this.sals[salIndex], actIndex)) {
                return;
            }
            const sal = this.sals[salIndex];
            const activity = sal.activities[actIndex];
            const product = findProduct(this.products, activity.product_id);
            if (product && product.type === 'overhead') {
                activity.unit = 'hours';
                if (!activity.cost_id && product.default_cost_id) {
                    activity.cost_id = product.default_cost_id;
                }
                if (!activity.title || !String(activity.title).trim()) {
                    activity.title = product.name;
                }
            }
            this.syncGroupIdentity(salIndex, actIndex);
            this.refreshSal(salIndex);
        },
        onGroupRevenueChange(salIndex, actIndex) {
            // Ricavo solo sulla primaria; le continuation restano a 0 in submit
            this.touch();
        },
        onGroupCommercialChange(salIndex, actIndex) {
            const sal = this.sals[salIndex];
            if (!this.isGroupPrimary(sal, actIndex)) {
                return;
            }
            const primary = sal.activities[actIndex];
            this.groupIndexes(sal, actIndex).forEach((idx) => {
                if (idx === actIndex) {
                    return;
                }
                const row = sal.activities[idx];
                row.is_optional = primary.is_optional;
                row.hide_price = primary.hide_price;
                row.notes = primary.notes;
                row.line_discount = 0;
                row.revenue = 0;
                row.recurring_period = primary.recurring_period;
                row.recurring_start_date = primary.recurring_start_date;
                row.recurring_end_date = primary.recurring_end_date;
                row.recurring_advance = primary.recurring_advance;
            });
            this.touch();
        },
        isOverhead(activity) {
            return isOverheadActivity(activity, this.products);
        },
        overheadPercent(activity) {
            const product = findProduct(this.products, activity.product_id);
            return product ? (parseFloat(product.standard_value) || 0) : 0;
        },
        isTimeBased(activity) {
            if (this.isOverhead(activity)) {
                return true;
            }
            const cost = findCost(this.costs, activity.cost_id);
            return !!(cost && !cost.is_fixed_cost && !cost.is_expense);
        },
        unitOptionsFor(activity) {
            if (this.isOverhead(activity)) {
                return [{ value: 'hours', label: 'Ore' }];
            }
            if (this.isTimeBased(activity)) {
                return this.units;
            }
            return [{ value: 'euro', label: 'Euro' }];
        },
        refreshSal(salIndex) {
            const sal = this.sals[salIndex];
            const updated = recalculateSalActivities(sal.activities, this.costs, this.products);
            this.$set(sal, 'activities', updated);
            this.$set(sal, 'activityBlocks', activitiesToBlocks(updated));
            this.touch();
        },
        onActivityMetaChange(salIndex, actIndex) {
            const sal = this.sals[salIndex];
            const activity = sal.activities[actIndex];
            if (this.isOverhead(activity)) {
                activity.unit = 'hours';
            } else if (!this.isTimeBased(activity)) {
                activity.unit = 'euro';
            } else if (activity.unit === 'euro' || !activity.unit) {
                activity.unit = 'hours';
            }
            this.refreshSal(salIndex);
        },
        addSal() {
            const sal = emptySal();
            sal.activities.push(emptyActivity(this.defaultProductId));
            sal.activityBlocks = activitiesToBlocks(sal.activities);
            this.sals.push(sal);
            this.touch();
        },
        removeSal(salIndex) {
            if (!window.confirm('Eliminare questo SAL?')) {
                return;
            }
            this.sals.splice(salIndex, 1);
            this.touch();
        },
        addActivity(salIndex) {
            this.sals[salIndex].activities.push(emptyActivity(this.defaultProductId));
            this.rebuildActivityBlocks(salIndex);
            this.touch();
        },
        removeActivity(salIndex, actIndex) {
            const key = this.sals[salIndex].activities[actIndex].client_key;
            this.sals[salIndex].activities.splice(actIndex, 1);
            if (this.openDetails[key]) {
                this.$delete(this.openDetails, key);
            }
            this.refreshSal(salIndex);
        },
        addResource(salIndex, actIndex) {
            const sal = this.sals[salIndex];
            if (!this.isGroupPrimary(sal, actIndex) || this.isOverhead(sal.activities[actIndex])) {
                return;
            }
            const source = sal.activities[actIndex];
            let groupId = activityGroupId(source);
            if (!groupId) {
                groupId = (window.crypto && typeof crypto.randomUUID === 'function')
                    ? crypto.randomUUID()
                    : ('xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
                        const r = Math.random() * 16 | 0;
                        const v = ch === 'x' ? r : (r & 0x3 | 0x8);
                        return v.toString(16);
                    }));
                this.$set(source, 'activity_group_id', groupId);
            }

            const clone = emptyActivity(source.product_id || this.defaultProductId);
            clone.product_id = source.product_id;
            clone.title = source.title;
            clone.activity_group_id = groupId;
            clone.unit = 'hours';
            clone.quantity = 0;
            clone.cost_id = null;
            clone.revenue = 0;
            clone.line_discount = 0;
            clone.notes = '';
            clone.is_optional = !!source.is_optional;
            clone.hide_price = false;
            clone.recurring_period = '';
            clone.recurring_start_date = '';
            clone.recurring_end_date = '';
            clone.recurring_advance = false;

            // Inserisci dopo l'ultima riga dello stesso gruppo
            const indexes = this.groupIndexes(sal, actIndex);
            const insertAt = Math.max(...indexes) + 1;
            sal.activities.splice(insertAt, 0, clone);
            this.rebuildActivityBlocks(salIndex);
            this.touch();
        },
    },
};
</script>

<style scoped>
.qce-sticky-anchor {
    margin: 0 0 8px;
}
.qce-sticky-stack {
    z-index: 20;
}
.qce-sticky-stack.is-pinned {
    position: fixed;
    z-index: 1020;
    margin: 0;
}
.qce-sticky-stack.is-pinned .qce-sticky {
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.28);
}
.qce-sticky {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
    align-items: flex-end;
    padding: 10px 14px;
    margin: 0;
    background: #1f2933;
    color: #fff;
    border-radius: 8px;
}
.qce-sticky--recurring {
    background: #243544;
}
.qce-sticky__kicker {
    min-width: 88px;
    flex: 0 0 auto;
}
.qce-sticky__scope {
    display: flex;
    gap: 4px;
    align-self: center;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    padding: 2px;
}
.qce-scope-btn {
    border: 0;
    background: transparent;
    color: rgba(255, 255, 255, 0.75);
    font-size: 12px;
    font-weight: 600;
    padding: 5px 10px;
    border-radius: 4px;
    line-height: 1.2;
}
.qce-scope-btn.is-active {
    background: #fff;
    color: #1f2933;
}
.qce-sticky__caption {
    margin: 6px 0 0;
    font-size: 11px;
    color: #64748b;
}
.qce-sticky__actions {
    margin-left: auto;
    align-self: center;
    display: flex;
    align-items: center;
    gap: 8px;
}
.qce-recalc-btn {
    color: #cbd5e1 !important;
    padding-left: 0;
    padding-right: 4px;
}
.qce-dirty {
    font-size: 11px;
    font-weight: 600;
    color: #fbbf24;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}
.qce-sticky__item {
    min-width: 72px;
    flex: 1;
}
.qce-sticky__input {
    width: 88px;
    height: 28px;
    color: #111;
}
.qce-sticky__label {
    display: block;
    font-size: 11px;
    text-transform: uppercase;
    opacity: 0.75;
    margin-bottom: 2px;
}
.qce-sticky__item--revenue strong {
    color: #7dd3fc;
}
.qce-sticky__item--ok strong {
    color: #86efac;
}
.qce-sticky__item--warn strong {
    color: #fcd34d;
}
.qce-sticky__item--bad strong {
    color: #fca5a5;
}
.qce-sticky__item--pure strong,
.qce-sticky__item--muted strong {
    color: #cbd5e1;
}
.qce-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1050;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}
.qce-modal {
    background: #fff;
    color: #333;
    border-radius: 4px;
    width: 100%;
    max-width: 480px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}
.qce-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
}
.qce-modal__header h4 {
    margin: 0;
    font-size: 16px;
}
.qce-modal__body {
    padding: 16px;
}
.qce-modal__footer {
    padding: 12px 16px;
    border-top: 1px solid #eee;
    text-align: right;
}
.qce-modal__footer .btn {
    margin-left: 8px;
}
.qce-modal__range {
    display: flex;
    align-items: center;
    gap: 12px;
}
.qce-modal__range input[type="range"] {
    flex: 1;
}
.qce-hint {
    margin-bottom: 16px;
    font-size: 12px;
}
.qce-sal {
    border: 1px solid #d9dee3;
    border-radius: 8px;
    margin-bottom: 16px;
    background: #fff;
    overflow: hidden;
}
.qce-sal--optional {
    border-style: dashed;
    border-color: #94a3b8;
}
.qce-sal--recurring:not(.qce-sal--optional) {
    border-color: #64748b;
}
.qce-sal--ghost {
    opacity: 0.55;
}
.qce-sal__header {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
    padding: 12px 14px;
    background: #333;
    color: #fff;
}
.qce-sal-drag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 34px;
    cursor: move;
    color: #fff;
    opacity: 0.85;
    flex: 0 0 auto;
    align-self: center;
}
.qce-sal-drag:hover {
    opacity: 1;
}
.qce-sal__title-wrap {
    flex: 1;
    min-width: 220px;
}
.qce-sal__title-wrap label {
    display: block;
    font-size: 12px;
    margin-bottom: 4px;
}
.qce-sal__flags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
}
.qce-sal__flags .checkbox-inline {
    color: #fff;
    margin: 0;
}
.qce-sal__flag--quiet {
    opacity: 0.7;
    font-weight: 400;
}
.qce-sal__delete {
    opacity: 0.45;
}
.qce-sal__header:hover .qce-sal__delete,
.qce-sal__delete:focus {
    opacity: 1;
}
.qce-period-badge {
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    background: #e2e8f0;
    border-radius: 999px;
    padding: 4px 10px;
    align-self: center;
}
.qce-sal__recurring {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 14px;
    background: #f5f7fa;
    border-bottom: 1px solid #e5e7eb;
}
.qce-sal__recurring .form-control {
    width: auto;
    min-width: 120px;
}
.qce-sal__apply-recurring {
    align-self: center;
}
.qce-activities {
    min-height: 4px;
}
.qce-colhead,
.qce-activity__main {
    display: grid;
    grid-template-columns:
        22px
        26px
        minmax(100px, 120px)
        minmax(160px, 1fr)
        minmax(100px, 130px)
        72px
        70px
        78px
        92px
        80px
        56px
        56px;
    gap: 8px;
    align-items: center;
}
.qce-colhead {
    padding: 6px 14px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #64748b;
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;
}
.qce-colhead--num {
    text-align: right;
}
.qce-activity-block--ghost {
    opacity: 0.55;
    background: #eef2f7;
}
.qce-activity {
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
}
.qce-activity--continuation {
    background: #f8fafc;
    box-shadow: inset 3px 0 0 #94a3b8;
}
.qce-activity--optional .qce-field-title {
    font-style: italic;
}
.qce-act-drag,
.qce-act-drag-spacer {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    color: #64748b;
}
.qce-act-drag {
    cursor: move;
}
.qce-act-drag:hover {
    color: #334155;
}
.qce-act-drag-spacer {
    visibility: hidden;
}
.qce-activity__toggle,
.qce-activity__toggle-spacer {
    width: 26px;
    height: 22px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
}
.qce-activity__toggle-spacer {
    visibility: hidden;
}
.qce-field-product,
.qce-field-title,
.qce-field-resource,
.qce-qty,
.qce-unit,
.qce-revenue,
.qce-discount {
    width: 100% !important;
    min-width: 0 !important;
    max-width: none !important;
}
.qce-money {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
}
.qce-money > span {
    font-size: 12px;
    color: #64748b;
    flex: 0 0 auto;
}
.qce-money--dark > span {
    color: rgba(255, 255, 255, 0.7);
}
.qce-money .form-control {
    flex: 1;
    min-width: 0;
    text-align: right;
}
.qce-discount-spacer {
    text-align: right;
    color: #cbd5e1;
}
.qce-field-product:disabled,
.qce-field-title[readonly] {
    background: #eef2f7;
    color: #475569;
    cursor: default;
}
.qce-cost,
.qce-gm {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    height: 30px;
    padding: 0 6px;
    font-size: 12px;
    white-space: nowrap;
}
.qce-cost {
    color: #475569;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
}
.qce-gm--ok {
    color: #15803d;
    font-weight: 600;
}
.qce-gm--warn {
    color: #b45309;
    font-weight: 600;
}
.qce-gm--bad {
    color: #b91c1c;
    font-weight: 600;
}
.qce-gm--muted,
.qce-gm--pure {
    color: #94a3b8;
}
.qce-revenue--shared {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    height: 30px;
    padding: 0 8px;
    font-size: 12px;
    color: #64748b;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    box-sizing: border-box;
}
.qce-qty:disabled,
.qce-qty[readonly] {
    background: #eef2f7;
    color: #475569;
    cursor: default;
}
.qce-activity__actions {
    display: flex;
    gap: 4px;
    justify-content: flex-end;
    width: 56px;
}
@media (hover: hover) {
    .qce-activity__actions {
        opacity: 0;
    }
    .qce-activity:hover .qce-activity__actions,
    .qce-activity:focus-within .qce-activity__actions {
        opacity: 1;
    }
}
.qce-action-spacer {
    display: inline-block;
    width: 22px;
    height: 22px;
    flex: 0 0 22px;
}
.qce-activity__details {
    margin-top: 10px;
    padding: 10px 12px;
    border-top: 1px dashed #d1d5db;
    background: #f3f4f6;
    border-radius: 6px;
}
.qce-activity__details-row,
.qce-activity__recurring {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
}
.qce-activity__details-row label {
    margin: 0;
    font-size: 12px;
    font-weight: normal;
}
.qce-activity__details-row .form-control {
    width: 110px;
    display: inline-block;
    margin-left: 4px;
}
.qce-computed {
    font-size: 12px;
    color: #555;
    white-space: nowrap;
}
.qce-notes-label {
    flex: 1;
    min-width: 160px;
}
.qce-notes-label .form-control {
    width: 100% !important;
    max-width: 320px;
}
.qce-activity__recurring {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e5e7eb;
}
.qce-activity__recurring .form-control {
    width: auto;
    min-width: 100px;
}
.qce-sal__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: #f9fafb;
}
.qce-sal__totals {
    font-size: 12px;
    color: #444;
    text-align: right;
}
.qce-sal__contract {
    display: block;
    margin-top: 2px;
    color: #334155;
    font-weight: 600;
}
.qce-pill {
    display: inline-block;
    margin-right: 8px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    vertical-align: middle;
}
.qce-pill--optional {
    background: #e2e8f0;
    color: #475569;
}
.qce-recalc-preview {
    margin-top: 8px;
    padding: 8px 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: 13px;
}
.qce-add-sal {
    margin-bottom: 16px;
}
.qce-hidden-fields {
    position: absolute;
    left: -9999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
}
</style>
